import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Warehouse as WarehouseIcon, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  ArrowRightLeft, 
  MapPin, 
  Building2, 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  SlidersHorizontal,
  Phone,
  Mail,
  User,
  X,
  ChevronLeft,
  ChevronRight,
  Layers,
  Sparkles,
  Pencil,
  Trash2
} from "lucide-react";
import toast from "react-hot-toast";
import ManageStockModal from "./ManageStockModal";
import StockLedgerModal from "./StockLedgerModal";

const API = process.env.REACT_APP_API_URL;

const WarehouseInventory = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingWh, setLoadingWh] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [stockLevelFilter, setStockLevelFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [categories, setCategories] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Modals
  const [isAddWhModalOpen, setIsAddWhModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  const [newWh, setNewWh] = useState({
    name: "",
    type: "Fulfilment Center",
    street: "",
    city: "",
    state: "",
    pincode: "",
    managerName: "",
    phone: "",
    email: "",
  });
  const [editingWhId, setEditingWhId] = useState(null);
  const [submittingWh, setSubmittingWh] = useState(false);

  // Fetch warehouses
  const fetchWarehouses = async () => {
    setLoadingWh(true);
    try {
      const res = await axios.get(`${API}/inventory/warehouses`);
      if (res.data.success) {
        const whList = res.data.warehouses || [];
        setWarehouses(whList);
        if (whList.length > 0) {
          // If none selected or previous selection no longer exists, select the first
          setSelectedWarehouse((prev) => {
            if (!prev) return whList[0];
            const found = whList.find((w) => w._id === prev._id || w.name === prev.name);
            return found || whList[0];
          });
        }
      }
    } catch (err) {
      console.error("Error fetching warehouses:", err);
      toast.error("Failed to load warehouses");
    } finally {
      setLoadingWh(false);
    }
  };

  // Fetch filter options (categories)
  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/inventory/filter-options`);
      if (res.data.success) {
        setCategories(res.data.categories || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    fetchFilterOptions();
  }, []);

  // Fetch products inside selected warehouse
  const fetchWarehouseProducts = useCallback(async () => {
    if (!selectedWarehouse) return;
    setLoadingProducts(true);

    try {
      const params = {
        scope: "warehouse",
        warehouse: selectedWarehouse.name,
        category: categoryFilter,
        status: statusFilter,
        stockLevel: stockLevelFilter,
        search: searchTerm,
        sortBy,
        sortOrder,
        page,
        limit,
      };

      const res = await axios.get(`${API}/inventory/items`, { params });
      if (res.data.success) {
        setProducts(res.data.products || []);
        setStats(res.data.stats || null);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error("Error fetching warehouse products:", err);
      toast.error("Failed to load warehouse stock");
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedWarehouse, categoryFilter, statusFilter, stockLevelFilter, searchTerm, sortBy, sortOrder, page, limit]);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchWarehouseProducts();
    }
  }, [fetchWarehouseProducts, selectedWarehouse]);

  // Open modal for editing
  const openEditModal = (wh, e) => {
    if (e) e.stopPropagation();
    setEditingWhId(wh._id);
    setNewWh({
      name: wh.name || "",
      type: wh.type || "Fulfilment Center",
      street: wh.address?.street || "",
      city: wh.address?.city || wh.city || "",
      state: wh.address?.state || wh.state || "",
      pincode: wh.address?.pincode || wh.pincode || "",
      managerName: wh.contact?.managerName || "",
      phone: wh.contact?.phone || "",
      email: wh.contact?.email || "",
    });
    setIsAddWhModalOpen(true);
  };

  // Delete warehouse dynamically
  const handleDeleteWarehouse = async (wh, e) => {
    if (e) e.stopPropagation();
    if (wh.productCount > 0) {
      toast.error(`Cannot delete "${wh.name}". It still contains ${wh.productCount} assigned products.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete warehouse "${wh.name}"?`)) return;

    try {
      const res = await axios.delete(`${API}/inventory/warehouses/${wh._id}`);
      if (res.data.success) {
        toast.success("Warehouse deleted successfully");
        if (selectedWarehouse?._id === wh._id) {
          setSelectedWarehouse(null);
        }
        await fetchWarehouses();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete warehouse");
    }
  };

  // Create or Update warehouse
  const handleCreateWarehouse = async (e) => {
    e.preventDefault();
    if (!newWh.name.trim()) {
      toast.error("Warehouse Name is required");
      return;
    }

    setSubmittingWh(true);
    try {
      const payload = {
        name: newWh.name.trim(),
        type: newWh.type,
        address: {
          street: newWh.street.trim(),
          city: newWh.city.trim(),
          state: newWh.state.trim(),
          pincode: newWh.pincode.trim(),
          country: "India",
        },
        contact: {
          managerName: newWh.managerName.trim(),
          phone: newWh.phone.trim(),
          email: newWh.email.trim(),
        },
      };

      let res;
      if (editingWhId) {
        res = await axios.put(`${API}/inventory/warehouses/${editingWhId}`, payload);
      } else {
        res = await axios.post(`${API}/inventory/warehouses`, payload);
      }

      if (res.data.success) {
        toast.success(editingWhId ? "Warehouse updated successfully!" : "New Warehouse created successfully!");
        setIsAddWhModalOpen(false);
        setEditingWhId(null);
        setNewWh({
          name: "",
          type: "Fulfilment Center",
          street: "",
          city: "",
          state: "",
          pincode: "",
          managerName: "",
          phone: "",
          email: "",
        });
        await fetchWarehouses();
      }
    } catch (err) {
      console.error("Error saving warehouse:", err);
      toast.error(err.response?.data?.message || "Failed to save warehouse");
    } finally {
      setSubmittingWh(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (products.length === 0) {
      toast.error("No products available to export");
      return;
    }

    const headers = ["Product Name", "SKU", "BHKG ID", "Warehouse", "Bin/Rack", "Available Stock", "Price (INR)", "Status", "Owner"];
    const rows = products.map((p) => [
      `"${(p.name?.en || p.name || "").replace(/"/g, '""')}"`,
      `"${p.sku || ""}"`,
      `"${p.bhkg_id || ""}"`,
      `"${p.warehouse || selectedWarehouse?.name || ""}"`,
      `"${p.rackBinLocation || "A-01-01"}"`,
      p.stock || 0,
      p.price || 0,
      p.stock_status || "active",
      p.uploadedBy === "admin" ? "Admin" : `Vendor (${p.supplier?.businessName || "Artisan"})`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `warehouse_${(selectedWarehouse?.warehouseId || "stock").toLowerCase()}_inventory.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exported warehouse inventory successfully!");
  };

  const getStatusColor = (status) => {
    if (status === "active") return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (status === "inactive") return "text-gray-700 bg-gray-100 border-gray-300";
    if (status === "out of stock") return "text-rose-700 bg-rose-50 border-rose-200";
    return "text-gray-700 bg-gray-100 border-gray-200";
  };

  return (
    <section className="min-h-screen bg-[#f3f4f6] p-4 sm:p-6 font-sans">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>Admin</span>
            <span>›</span>
            <span>Inventory</span>
            <span>›</span>
            <span className="text-[#b07d17] font-semibold">Warehouse Stock Hub</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-philosopher text-gray-900 flex items-center gap-3">
            <span className="p-2 rounded-xl bg-gradient-to-br from-[#131921] to-[#232f3e] text-[#febd69] shadow-md">
              <WarehouseIcon className="w-6 h-6" />
            </span>
            Warehouse Inventory & Fulfilment Hubs
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Location-wise physical stock control. Select any warehouse below to audit on-hand stock, assign storage rack bins, restock, or initiate inter-warehouse transfers.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { fetchWarehouses(); fetchWarehouseProducts(); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 font-medium shadow-sm transition"
          >
            <RefreshCw size={15} className={loadingWh || loadingProducts ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 font-medium shadow-sm transition"
          >
            <Download size={15} /> Export CSV
          </button>
          <button
            onClick={() => {
              setEditingWhId(null);
              setNewWh({
                name: "",
                type: "Fulfilment Center",
                street: "",
                city: "",
                state: "",
                pincode: "",
                managerName: "",
                phone: "",
                email: "",
              });
              setIsAddWhModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-sm font-semibold shadow-md transition"
          >
            <Plus size={16} /> Add New Warehouse
          </button>
        </div>
      </div>

      {/* Warehouse Selection Cards Carousel / Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <Building2 size={16} className="text-[#b07d17]" />
            Select Warehouse / Fulfilment Center ({warehouses.length})
          </h2>
          <span className="text-xs text-gray-500">
            Click to view and manage stock in that location
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {warehouses.length === 0 ? (
            <div className="col-span-full p-8 text-center bg-white rounded-xl border border-dashed border-gray-300">
              <Building2 className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <h3 className="text-base font-bold text-gray-800">No Warehouses Configured</h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">Click "Add New Warehouse" to create one dynamically.</p>
              <button
                onClick={() => {
                  setEditingWhId(null);
                  setIsAddWhModalOpen(true);
                }}
                className="px-4 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-xs font-semibold"
              >
                + Add New Warehouse
              </button>
            </div>
          ) : (
            warehouses.map((wh) => {
              const isSelected = selectedWarehouse?._id === wh._id || selectedWarehouse?.warehouseId === wh.warehouseId;
              return (
                <div
                  key={wh._id || wh.warehouseId}
                  onClick={() => { setSelectedWarehouse(wh); setPage(1); }}
                  className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 border text-left shadow-sm ${
                    isSelected
                      ? "bg-white border-[#b07d17] ring-2 ring-[#b07d17]/40 shadow-md"
                      : "bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                  }`}
                >
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    {isSelected && (
                      <span className="text-xs bg-[#b07d17] text-white px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <CheckCircle size={12} /> Active
                      </span>
                    )}
                    <button
                      onClick={(e) => openEditModal(wh, e)}
                      className="p-1 text-gray-400 hover:text-[#b07d17] hover:bg-gray-100 rounded"
                      title="Edit Warehouse"
                    >
                      <Pencil size={13} />
                    </button>
                    {wh.productCount === 0 && (
                      <button
                        onClick={(e) => handleDeleteWarehouse(wh, e)}
                        className="p-1 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                        title="Delete Warehouse"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  
                  <div className="text-xs font-mono font-semibold text-gray-500 mb-1">
                    {wh.warehouseId}
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 line-clamp-1 mb-1 pr-14" title={wh.name}>
                    {wh.name}
                  </h3>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                    <MapPin size={13} className="text-gray-400 shrink-0" />
                    <span className="truncate">{wh.address?.city || wh.city || "India"}, {wh.address?.state || wh.state || ""}</span>
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-gray-400">Total Stock</div>
                      <div className="text-base font-extrabold text-gray-900">
                        {wh.totalStock ?? "--"} <span className="text-xs font-normal text-gray-500">units</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-gray-400">Unique SKUs</div>
                      <div className="text-base font-bold text-[#007185]">
                        {wh.productCount ?? "--"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Selected Warehouse Overview Detail Banner */}
      {selectedWarehouse && (
        <div className="bg-gradient-to-r from-[#131921] via-[#1a2330] to-[#232f3e] rounded-xl text-white p-5 mb-6 shadow-md border border-gray-800">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded text-xs font-mono bg-[#febd69] text-black font-bold">
                  {selectedWarehouse.warehouseId}
                </span>
                <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-gray-300">
                  {selectedWarehouse.type || "Fulfilment Center"}
                </span>
                <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Operational
                </span>
              </div>
              <h2 className="text-xl font-bold text-white">
                {selectedWarehouse.name}
              </h2>
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-300">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#febd69]" />
                  {selectedWarehouse.address?.street ? `${selectedWarehouse.address.street}, ` : ""}
                  {selectedWarehouse.address?.city || selectedWarehouse.city}, {selectedWarehouse.address?.state || selectedWarehouse.state} - {selectedWarehouse.address?.pincode || selectedWarehouse.pincode}
                </span>
                {selectedWarehouse.contact?.managerName && (
                  <span className="flex items-center gap-1.5">
                    <User size={14} className="text-[#febd69]" />
                    Manager: {selectedWarehouse.contact.managerName}
                  </span>
                )}
                {selectedWarehouse.contact?.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone size={14} className="text-[#febd69]" />
                    {selectedWarehouse.contact.phone}
                  </span>
                )}
              </div>
            </div>

            {/* Warehouse Stat Pills */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                <div className="text-xs text-gray-300">Total Units</div>
                <div className="text-xl font-black text-[#febd69] mt-0.5">
                  {stats?.totalUnits ?? selectedWarehouse.totalStock ?? 0}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                <div className="text-xs text-gray-300">Active SKUs</div>
                <div className="text-xl font-bold text-white mt-0.5">
                  {stats?.totalProducts ?? selectedWarehouse.productCount ?? 0}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                <div className="text-xs text-gray-300">Low Stock</div>
                <div className="text-xl font-bold text-amber-400 mt-0.5">
                  {stats?.lowStockCount ?? 0}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                <div className="text-xs text-gray-300">Out of Stock</div>
                <div className="text-xl font-bold text-rose-400 mt-0.5">
                  {stats?.outOfStockCount ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Control Bar: Search & Dynamic Filters for this Warehouse */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <input
              type="text"
              placeholder="Search SKU, BHKG ID, Product Title..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
            >
              <option value="All">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stock Level Filter */}
          <div>
            <select
              value={stockLevelFilter}
              onChange={(e) => { setStockLevelFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
            >
              <option value="all">All Stock Levels</option>
              <option value="in">In Stock (&gt; 0)</option>
              <option value="low">Low Stock (1-20)</option>
              <option value="out">Out of Stock (0)</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
            >
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="out of stock">Out of Stock</option>
            </select>
          </div>
        </div>
      </div>

      {/* Warehouse Product Stock Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#f8f9fa] border-b border-gray-200 text-gray-700">
              <tr>
                <th className="px-4 py-3.5 font-semibold">Image</th>
                <th className="px-4 py-3.5 font-semibold">Product Title & ASIN/SKU</th>
                <th className="px-4 py-3.5 font-semibold">Ownership</th>
                <th className="px-4 py-3.5 font-semibold">Storage Bin / Rack</th>
                <th className="px-4 py-3.5 font-semibold text-right">Available Qty</th>
                <th className="px-4 py-3.5 font-semibold text-center">Status</th>
                <th className="px-4 py-3.5 font-semibold text-center">Admin Stock Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loadingProducts ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#b07d17] mx-auto mb-2" />
                    <span>Loading warehouse inventory...</span>
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((p) => {
                  const prodName = p.name?.en || (typeof p.name === "string" ? p.name : "--");
                  const img = p.images?.[0];
                  const stockQty = p.stock || 0;

                  return (
                    <tr key={p._id} className="hover:bg-gray-50/80 transition">
                      {/* Image */}
                      <td className="px-4 py-3">
                        <div className="w-12 h-12 rounded border border-gray-200 p-1 bg-white flex items-center justify-center shrink-0">
                          {img ? (
                            <img src={img} alt="product" className="max-w-full max-h-full object-contain" />
                          ) : (
                            <span className="text-[10px] text-gray-400">No Img</span>
                          )}
                        </div>
                      </td>

                      {/* Product Title & Identifiers */}
                      <td className="px-4 py-3">
                        <a
                          href={`/product/${p._id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-[#007185] hover:text-[#c7511f] hover:underline line-clamp-1 max-w-[280px]"
                          title={prodName}
                        >
                          {prodName}
                        </a>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span className="font-mono bg-gray-100 px-1 py-0.5 rounded text-[11px]">
                            SKU: {p.sku || "--"}
                          </span>
                          <span>•</span>
                          <span className="font-mono text-gray-600">ID: {p.bhkg_id || "--"}</span>
                          {p.category?.name && (
                            <>
                              <span>•</span>
                              <span className="text-gray-500">{typeof p.category.name === "object" ? p.category.name.en : p.category.name}</span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Ownership */}
                      <td className="px-4 py-3">
                        {p.uploadedBy === "admin" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                            Admin Direct
                          </span>
                        ) : (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                              Vendor: {p.supplier?.businessName || p.supplier?.fullName || "Artisan"}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Bin / Rack Location */}
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs px-2 py-1 bg-gray-100 rounded border border-gray-200 text-gray-800">
                          {p.rackBinLocation || "A-01-01"}
                        </span>
                      </td>

                      {/* Available Qty */}
                      <td className="px-4 py-3 text-right">
                        <div className={`text-base font-extrabold ${stockQty > 20 ? "text-emerald-700" : stockQty > 0 ? "text-amber-700" : "text-rose-700"}`}>
                          {stockQty}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {stockQty > 20 ? "Healthy" : stockQty > 0 ? "Low Stock" : "Depleted"}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wider ${getStatusColor(p.stock_status)}`}>
                          {p.stock_status || "active"}
                        </span>
                      </td>

                      {/* Admin Actions */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => { setActiveProduct(p); setIsManageModalOpen(true); }}
                            className="bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm transition flex items-center gap-1"
                            title="Manage Warehouse Stock"
                          >
                            <SlidersHorizontal size={13} /> Manage Stock
                          </button>
                          <button
                            onClick={() => { setActiveProduct(p); setIsLedgerModalOpen(true); }}
                            className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-lg px-2 py-1 text-xs font-medium transition"
                            title="View Audit Ledger"
                          >
                            Ledger
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium">No products match this warehouse filter.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      You can add stock or transfer existing products to {selectedWarehouse?.name}.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loadingProducts && products.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} SKUs
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).slice(
                Math.max(0, page - 3),
                Math.min(pagination.totalPages, page + 2)
              ).map((pNum) => (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    page === pNum
                      ? "bg-[#b07d17] text-white shadow-sm"
                      : "border border-gray-300 hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {pNum}
                </button>
              ))}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL 1: ADD NEW WAREHOUSE */}
      {isAddWhModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg overflow-hidden animate-in fade-in">
            <div className="bg-gradient-to-r from-[#131921] to-[#232f3e] px-5 py-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WarehouseIcon className="w-5 h-5 text-[#febd69]" />
                <h3 className="text-base font-bold text-white">
                  {editingWhId ? "Edit Warehouse Details" : "Create New Warehouse / Hub"}
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsAddWhModalOpen(false);
                  setEditingWhId(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateWarehouse} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Warehouse Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Kolkata East Logistics Depot"
                  value={newWh.name}
                  onChange={(e) => setNewWh({ ...newWh, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Warehouse Type</label>
                <select
                  value={newWh.type}
                  onChange={(e) => setNewWh({ ...newWh, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                >
                  <option value="Main">Main Distribution Hub</option>
                  <option value="Fulfilment Center">Fulfilment Center (FBA)</option>
                  <option value="Branch">Regional Branch</option>
                  <option value="Vendor Setup">Vendor Consignment Center</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    placeholder="e.g. Kolkata"
                    value={newWh.city}
                    onChange={(e) => setNewWh({ ...newWh, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    placeholder="e.g. West Bengal"
                    value={newWh.state}
                    onChange={(e) => setNewWh({ ...newWh, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Street / Address</label>
                  <input
                    type="text"
                    placeholder="Industrial Area, Gate 2"
                    value={newWh.street}
                    onChange={(e) => setNewWh({ ...newWh, street: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    placeholder="700001"
                    value={newWh.pincode}
                    onChange={(e) => setNewWh({ ...newWh, pincode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Manager Name</label>
                  <input
                    type="text"
                    placeholder="Amit Sengupta"
                    value={newWh.managerName}
                    onChange={(e) => setNewWh({ ...newWh, managerName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    placeholder="+91 9830..."
                    value={newWh.phone}
                    onChange={(e) => setNewWh({ ...newWh, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="wh@bharat.com"
                    value={newWh.email}
                    onChange={(e) => setNewWh({ ...newWh, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddWhModalOpen(false);
                    setEditingWhId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingWh}
                  className="px-5 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-sm font-semibold shadow-sm transition disabled:opacity-50"
                >
                  {submittingWh ? "Saving..." : (editingWhId ? "Update Warehouse" : "Create Warehouse")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: MANAGE STOCK IN WAREHOUSE */}
      <ManageStockModal
        isOpen={isManageModalOpen}
        onClose={() => { setIsManageModalOpen(false); setActiveProduct(null); }}
        product={activeProduct}
        warehouses={warehouses}
        onStockUpdated={() => {
          fetchWarehouseProducts();
          fetchWarehouses();
        }}
      />

      {/* MODAL 3: AUDIT LEDGER */}
      <StockLedgerModal
        isOpen={isLedgerModalOpen}
        onClose={() => { setIsLedgerModalOpen(false); setActiveProduct(null); }}
        product={activeProduct}
      />
    </section>
  );
};

export default WarehouseInventory;
